<template>
  <div>

    <div v-if="showFilters" class="users-table-filters row">
      <div class="col-12 col-md-4">
        <label class="form-label">{{ $t('entities.activities.status_of_approval') }}</label>
        <multiselect
          class="multiselect__filters"
          v-model="filters.statusesOfApproval"
          :options="statusesOfApprovalOptions"
          track-by="id"
          label="name"
          :customLabel="option => $t(`entities.activities.statuses_of_approval.${option.id}`, option.name)"
          select-label=""
          selected-label=""
          deselect-label=""
          placeholder="---"
          :allow-empty="true"
          :multiple="true"
          :searchable="true"
        >
          <template slot="clear" slot-scope="props">
            <div class="multiselect__clear" v-if="filters.statusesOfApproval.length"
                 @mousedown.prevent.stop="filters.statusesOfApproval = []"></div>
          </template>
          <template slot="option" slot-scope="props">
            <span>{{ $t(`entities.activities.statuses_of_approval.${props.option.id}`, props.option.name) }}</span>
          </template>
          <template slot="singleLabel" slot-scope="{option}" class="d-flex align-items-center justify-content-center">
            <span>{{ $t(`evocall.priorities.${option.id}`, option.name) }}</span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-4">
        <label class="form-label">{{ $t('entities.activities.active') }}</label>
        <multiselect
          class="multiselect__filters"
          v-model="filters.active"
          :options="activeOptions"
          track-by="id"
          :customLabel="option => $t(`rules.labels.${option.id}`)"
          select-label=""
          selected-label=""
          deselect-label=""
          placeholder="---"
          :allow-empty="true"
          :multiple="false"
          :searchable="true"
        >
          <template slot="clear" slot-scope="props">
            <div class="multiselect__clear" v-if="filters.active" @mousedown.prevent.stop="filters.active = null"></div>
          </template>
        </multiselect>
      </div>
    </div>

    <v-server-table
      :columns="columns"
      :options="tableOptions"
      id="user-activities-table"
      ref="userActivitiesTable"
      name="userActivitiesTable">

      <span slot="activity.name" slot-scope="{row}">
        <span v-if="row.activity">
          <span>{{ $t(`evocall.activities.${row.activity.id}`, row.activity.name) }}</span>
        </span>
      </span>

      <span slot="status_id" class="no-break" slot-scope="props">
        <span class="small-status-icon align-middle mr-1"
              :class="{'i-evollu-status-active': props.row.status_id === 1, 'i-evollu-status-pending': props.row.status_id === 2, 'i-evollu-status-inactive': [3, 4].includes(props.row.status_id)}">
        </span>
      </span>

      <span slot="start_date" slot-scope="{row}">
        <span v-if="row.start_date">
          {{ row.start_date | localTime('YYYY-MM-DD HH:mm') }}
        </span>
      </span>
      <span slot="end_date" slot-scope="{row}">
        <span v-if="row.end_date">
          {{ row.end_date | localTime('YYYY-MM-DD HH:mm') }}
        </span>
      </span>
      <span slot="active" slot-scope="{row}">
        {{ checkActivityIsActive(row) }}
      </span>
      <span slot="actions" slot-scope="props" v-if="editableStatusesOfApprovalOptions.includes(props.row.status_id)">
        <a href="#" @click.prevent="$emit('edit', props.row)">
          <i class="fa fa-pencil"></i>
        </a>
        <a href="#" @click.prevent="$emit('delete', props.row)">
          &nbsp;<i class="fa fa-trash"></i>
        </a>
      </span>
    </v-server-table>
  </div>
</template>
<script>
import Multiselect from 'vue-multiselect'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import { ACTIVITY_STATUSES_OF_APPROVAL } from './config'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { FILTERS_ENTITY_ACTIVITY_USER_TABLE } from '@/local-storage/LocalStorageKeys'

const ActiveActivityStatus = 1
const PendingActivityStatus = 2

export default {
  components: {
    Multiselect
  },
  props: {
    columns: {
      type: Array,
      required: false,
      default: () => ['entity.name', 'activity.name', 'status_id', 'start_date', 'end_date', 'active', 'actions']
    },
    statusesOfApproval: {
      type: Array,
      required: false,
      default: () => []
    },
    user: {
      type: Object,
      required: false
    },
    entity: {
      type: Object,
      required: false
    },
    showFilters: {
      type: Boolean,
      required: false,
    }
  },
  mixins: [
    TableQueryMixin
  ],
  data () {
    return {
      tableOptions: {
        filterable: true,
        columnsDropdown: false,
        saveState: false,
        sortable: ['activity.name', 'entity.name', 'status_id', 'start_date', 'end_date'],
        headings: {
          'activity.name': this.$t('users.activities.activity'),
          'entity.name': this.$t('facility.inputs.entity'),
          'user.name': this.$t('entities.roles.header_name'),
          status_id: this.$t('entities.activities.status_of_approval'),
          start_date: this.$t('licenses.start_date'),
          end_date: this.$t('licenses.end_date'),
          active: this.$t('entities.activities.active'),
          actions: ''
        },
        customFilters: ['statusesOfApproval', 'active'],
        columnsClasses: {
          'entity.name': 'entity_name',
          'activity.name': 'activity_name',
          'user.name': this.$t('entities.roles.header_name'),
          'status_id': 'text-center',
          start_date: 'start_date',
          end_date: 'end_date',
          active: 'text-center',
          action: 'action'
        },
        orderBy: {
          ascending: false,
          column: 'start_date'
        }
      },
      filters: {
        statusesOfApproval: [],
        active: {}
      },
      statusesOfApprovalOptions: ACTIVITY_STATUSES_OF_APPROVAL,
      activeOptions: [ // TODO: Change this approach of options with id's 0 and 1
        {
          id: 0,
          name: 'not_active'
        },
        {
          id: 1,
          name: 'active'
        }
      ],
      editableStatusesOfApprovalOptions: [ActiveActivityStatus, PendingActivityStatus]
    }
  },
  mounted () {
    this.loadLocalStorage()
  },
  methods: {
    setTableFilter: function (filter) {
      this.$refs.userActivitiesTable.setFilter(filter)
    },
    reload: function () {
      this.$refs.userActivitiesTable.refresh()
    },
    checkActivityIsActive: function (activity) {
      // Handle expired
      //|| (activity.start_date !== null && this.moment(activity.start_date).isAfter(this.moment()))
      if (this.moment(activity.start_date).isAfter(this.moment()) || (activity.end_date !== null && this.moment(activity.end_date).isBefore(this.moment())) || activity.status_id !== 1) {
        return (this.$t('rules.labels.0'))
      }
      // Handle Active
      else if (activity.end_date === null || this.moment(activity.end_date).isSameOrAfter(this.moment()) && activity.status_id === 1) {
        return (this.$t('rules.labels.1'))
      } else {
        return 'N/A'
      }
    },
    loadLocalStorage: function () {
      const filters = LocalStorageHelper.get(FILTERS_ENTITY_ACTIVITY_USER_TABLE)
      if (filters) {
        this.filters.active = filters.active

        if (this.filters.active) {
          this.$emit('toggle-filters')
        }
      }
    },
    saveLocalStorage: function () {
      LocalStorageHelper.set(FILTERS_ENTITY_ACTIVITY_USER_TABLE, {
        active: this.filters.active,
      })
    },
  },
  computed: {
    queryUrl: function () {
      let url = `${this.$store.state.evollauth_server}/entities-activities-users?infoType=140&nullUser=false`
      if (this.user) {
        url += `&users=${this.user.id}`
      }
      if (this.entity) {
        url += `&entities=${this.entity.id}`
      }
      return url
    },
    activeFilters: function () {
      // Throws error during initialization...
      if (this.filters !== null && this.filters !== undefined) {
        return Object.keys(this.filters).reduce((accumulator, currentValue) => accumulator + (Array.isArray(this.filters[currentValue]) ? (this.filters[currentValue].length > 0 ? 1 : 0) : typeof this.filters[currentValue] === 'object' ? Object.values(this.filters[currentValue]).some(value => value) ? 1 : 0 : this.filters[currentValue] !== '' ? 1 : 0), 0)
      } else {
        return 0
      }
    }
  },
  watch: {
    activeFilters: function (nrFilters) {
      this.$emit('filters-updated', nrFilters)
    },
    'filters.active': function (value) {
      if (value !== null) {
        this.emitTableFilter('vue-tables.userActivitiesTable.filter::active', value.id)
      } else {
        this.emitTableFilter('vue-tables.userActivitiesTable.filter::active', null)
      }

      this.saveLocalStorage()
    },
    'filters.statusesOfApproval': function (statusesOfApproval) {
      this.emitTableFilter('vue-tables.userActivitiesTable.filter::statusesOfApproval', statusesOfApproval.map(status => status.id).join(','))
    },
    statusesOfApproval: function (statusesOfApproval) {
      if (statusesOfApproval.length) {
        this.filters.statusesOfApproval = this.statusesOfApprovalOptions.filter(status => statusesOfApproval.some(_status => _status === status.id))
      }
    },
    entity: function () {
      this.$refs.userActivitiesTable.refresh()
    }
  }
}
</script>

<style lang="scss">

$assetsPath: "../../assets";
@import '@/sass/icons/icons-classes.scss';

#user-activities-table {
  tr {
    cursor: pointer;

    &:hover {
      .i-evollu-status-inactive {
        @extend .i-evollu-status-inactive-white;
      }

      .i-evollu-status-pending {
        @extend .i-evollu-status-pending-white;
      }

      .i-evollu-status-active {
        @extend .i-evollu-status-active-white;
      }
    }
  }
}

.multiselect__filters {
  max-width: 400px;
}

</style>
